/**
 * Loading skeleton shown while a conversation is fetching.
 *
 * Same layout grammar as the real ConversationView (header strip, message
 * bubbles, composer space at the bottom) so the transition doesn't jolt
 * the user. Pulses gently via Tailwind's `animate-pulse`.
 */
export function ConversationSkeleton() {
  return (
    <div className="flex h-full flex-col" aria-hidden="true">
      {/* Header strip — matches the real ConversationView's glass cream
          chrome so the load → ready transition doesn't flash a different
          background color. */}
      <div
        className="flex items-center justify-between border-b border-slate-900/[0.06] px-4 py-3.5 backdrop-blur-xl sm:px-7 sm:py-4"
        style={{ background: 'rgba(244, 243, 238, 0.78)' }}
      >
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-[14px] bg-slate-300/70 animate-pulse" />
          <div className="space-y-1.5">
            <div className="h-4 w-28 rounded bg-slate-300/70 animate-pulse" />
            <div className="h-3 w-20 rounded bg-slate-200/70 animate-pulse" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-7 w-16 rounded bg-slate-200/70 animate-pulse" />
          <div className="h-7 w-12 rounded bg-slate-200/70 animate-pulse" />
        </div>
      </div>

      {/* Message bubbles — over the same cream surface the real view
          uses, just without the persona radial tint (don't know which
          persona we'll land on yet). */}
      <div
        className="flex-1 overflow-hidden px-3 py-4 sm:px-6 sm:py-6"
        style={{ backgroundColor: '#f4f3ee' }}
      >
        <div className="mx-auto w-full max-w-3xl space-y-4">
          {/* User bubble (right-aligned, accent background) */}
          <div className="flex justify-end">
            <div className="h-12 w-2/3 sm:w-1/2 rounded-2xl bg-wb-200 animate-pulse" />
          </div>
          {/* Assistant bubble (left-aligned, neutral) */}
          <div className="flex justify-start">
            <div className="space-y-2 w-full sm:w-3/4">
              <div className="h-4 w-11/12 rounded bg-slate-200 animate-pulse" />
              <div className="h-4 w-10/12 rounded bg-slate-200 animate-pulse" />
              <div className="h-4 w-7/12 rounded bg-slate-200 animate-pulse" />
            </div>
          </div>
          {/* Another user bubble */}
          <div className="flex justify-end">
            <div className="h-10 w-1/2 rounded-2xl bg-wb-200 animate-pulse" />
          </div>
          {/* Another assistant bubble */}
          <div className="flex justify-start">
            <div className="space-y-2 w-full sm:w-3/4">
              <div className="h-4 w-9/12 rounded bg-slate-200 animate-pulse" />
              <div className="h-32 w-full rounded-lg bg-slate-100 animate-pulse" />
            </div>
          </div>
        </div>
      </div>

      {/* Composer placeholder */}
      <div
        className="border-t border-slate-900/[0.06] p-3 sm:p-4"
        style={{ background: 'rgba(244, 243, 238, 0.78)' }}
      >
        <div className="mx-auto w-full max-w-3xl">
          <div className="h-12 w-full rounded-md bg-slate-200/60 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
